import type { TemplateKind, TemplatePurpose } from '@brush/api-client';
import { FileText, Trophy, Upload, Users, type LucideIcon } from 'lucide-react';

/**
 * What a template is for, in the server's canonical order.
 *
 * Distinct from the features beside it in the same dialog: features decide *who* sees a
 * template, purpose decides *what happens* when they use it. `general` is a real answer —
 * a template nothing on the platform reads — and is the default.
 *
 * `kinds` mirrors the server's rule rather than replacing it. Keeping a purpose off the menu
 * for the wrong kind is a convenience; `TemplatePurposes.ParseFor` is the control.
 */
export const PURPOSES: {
  value: TemplatePurpose;
  label: string;
  description: string;
  icon: LucideIcon;
  kinds: TemplateKind[];
}[] = [
  {
    value: 'general',
    label: 'General',
    description: 'Nothing on the platform reads it — a document a room fills in and uses itself.',
    icon: FileText,
    kinds: ['html', 'csv'],
  },
  {
    value: 'import_tournaments',
    label: 'Import tournaments',
    description:
      'Offered to a room importing a file of tournaments. The fields below are the columns such a file may carry; each room says which of them its own file has.',
    icon: Upload,
    kinds: ['csv'],
  },
  {
    value: 'import_tournament_results',
    label: 'Import tournament results',
    description:
      'Offered to a room importing a file of finishing positions into tournaments it already holds. A row here is one player’s finish, not a tournament — so this is a different file from the one above, and a different template.',
    icon: Trophy,
    kinds: ['csv'],
  },
  {
    value: 'import_players',
    label: 'Import players',
    description:
      'Read by the player import. There is exactly one: its seven columns are fixed, and you can rename them and change their notes. Each room chooses which of the optional ones its files carry; a room that has not chosen reads them all.',
    icon: Users,
    kinds: ['csv'],
  },
];

export const purposeLabel = (value: string): string =>
  PURPOSES.find((p) => p.value === value)?.label ?? value;

/** The purposes a template of this kind may take. Importing reads columns, so it needs CSV. */
export const purposesFor = (kind: TemplateKind) => PURPOSES.filter((p) => p.kinds.includes(kind));
